import { modelsApi } from '@/api/models.api';
import type { StepDocumentModel } from '@/types/project.types';
import { useCallback, useEffect, useState } from 'react';

/**
 * Load the project's one STEP document by its id (`GET /3d/{model_id}`).
 *
 * `modelId` is `null` until a STEP has been uploaded; in that state the hook settles immediately
 * with no document rather than firing a request that would 404.
 */
export const useStepDocument = (modelId: string | null) => {
  const [stepDocument, setStepDocument] = useState<StepDocumentModel | null>(null);
  const [isLoading, setIsLoading] = useState(modelId !== null);
  const [error, setError] = useState<string | null>(null);

  const fetchStepDocument = useCallback(async () => {
    if (!modelId) {
      setStepDocument(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      setStepDocument(await modelsApi.get(modelId));
    } catch (err) {
      console.error('Failed to load the STEP document:', err);
      setStepDocument(null);
      setError('materials.stepDocumentLoadFailed');
    } finally {
      setIsLoading(false);
    }
  }, [modelId]);

  useEffect(() => {
    fetchStepDocument();
  }, [fetchStepDocument]);

  return { stepDocument, isLoading, error, refetch: fetchStepDocument };
};
